/**
 * Dialog "Sincronizar do Flow" — traz nodes de mensagem dos funis pra
 * biblioteca como templates.
 *
 * Escolhe a pasta destino (no projeto atual) + os funis de origem (todos ou
 * selecionados). Idempotente: re-sincronizar atualiza os já trazidos
 * (rastreio por node de origem), sem duplicar.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, Check, Search } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import {
    getFolderTree, createFolder, listFlowFunnels, syncTemplatesFromFlow,
} from '@/services/api';
import { flattenFolders } from './FolderTree';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    projectId: number | null;
    /** Pasta selecionada na aba — vira destino default. */
    defaultFolderId: number | null;
    onSynced?: () => void;
}

export default function SyncFromFlowDialog({
    open, onOpenChange, projectId, defaultFolderId, onSynced,
}: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [folderId, setFolderId] = useState<number | null>(defaultFolderId);
    const [selectedFunnels, setSelectedFunnels] = useState<Set<number>>(new Set());
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            setFolderId(defaultFolderId);
            setSelectedFunnels(new Set());
            setSearch('');
        }
    }, [open, defaultFolderId]);

    const { data: funnelsResp, isLoading: funnelsLoading } = useQuery({
        queryKey: ['flow-funnels'],
        queryFn: listFlowFunnels,
        enabled: open,
        staleTime: 1000 * 30,
    });
    const funnels = funnelsResp?.funnels ?? [];

    const { data: tree } = useQuery({
        queryKey: ['folder-tree', projectId],
        queryFn: () => getFolderTree(projectId),
        enabled: open,
        staleTime: 1000 * 15,
    });
    const flatFolders = flattenFolders(tree?.folders ?? []);

    const filteredFunnels = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? funnels.filter((f) => f.nome.toLowerCase().includes(q)) : funnels;
    }, [funnels, search]);

    const allSelected = filteredFunnels.length > 0
        && filteredFunnels.every((f) => selectedFunnels.has(f.id));
    const toggleAll = () => {
        setSelectedFunnels((prev) => {
            const next = new Set(prev);
            if (allSelected) filteredFunnels.forEach((f) => next.delete(f.id));
            else filteredFunnels.forEach((f) => next.add(f.id));
            return next;
        });
    };
    const toggleFunnel = (id: number) => {
        setSelectedFunnels((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const totalNodes = useMemo(
        () => funnels.filter((f) => selectedFunnels.has(f.id))
            .reduce((s, f) => s + f.node_count, 0),
        [funnels, selectedFunnels],
    );

    const mutation = useMutation({
        mutationFn: () => syncTemplatesFromFlow({
            project_id: projectId,
            folder_id: folderId,
            funnel_ids: Array.from(selectedFunnels),
        }),
        onSuccess: (r) => {
            const parts: string[] = [];
            if (r.created) parts.push(`${r.created} criados`);
            if (r.updated) parts.push(`${r.updated} atualizados`);
            if (r.skipped) parts.push(`${r.skipped} ignorados`);
            toast({
                title: 'Sincronizado do Flow',
                description: parts.length ? parts.join(' · ') + '.' : 'Nada pra sincronizar.',
            });
            queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
            queryClient.invalidateQueries({ queryKey: ['folder-tree'] });
            onSynced?.();
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            title: 'Falha ao sincronizar',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader className="min-w-0">
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-violet-600 shrink-0" /> Sincronizar do Flow
                    </DialogTitle>
                    <DialogDescription>
                        Traz os modelos de mensagem dos funis pra biblioteca. Re-sincronizar
                        atualiza os já trazidos (não duplica).
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 min-w-0">
                    {/* Pasta destino */}
                    <div className="space-y-1.5">
                        <Label>Pasta destino</Label>
                        <SearchableSelect
                            value={folderId == null ? '' : String(folderId)}
                            onValueChange={(v) => setFolderId(v ? Number(v) : null)}
                            options={[
                                { value: '', label: 'Raiz (sem pasta)' },
                                ...flatFolders.map((f) => ({
                                    value: String(f.id),
                                    label: `${'— '.repeat(f.depth)}${f.name}`,
                                })),
                            ]}
                            placeholder="Raiz (sem pasta)"
                            searchPlaceholder="Buscar ou criar pasta..."
                            createLabel="Criar pasta"
                            onCreate={async (name) => {
                                const r = await createFolder({ name, project_id: projectId, parent_id: null });
                                await queryClient.invalidateQueries({ queryKey: ['folder-tree', projectId] });
                                return { value: String(r.id ?? ''), label: name };
                            }}
                        />
                    </div>

                    {/* Funis de origem */}
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <Label>Funis de origem</Label>
                            {filteredFunnels.length > 0 && (
                                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                                    Todos
                                </label>
                            )}
                        </div>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar funil..." className="pl-8 h-9"
                            />
                        </div>

                        {funnelsLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                            </div>
                        ) : filteredFunnels.length === 0 ? (
                            <p className="text-xs text-gray-400 italic py-4 text-center">
                                Nenhum funil com modelos de mensagem.
                            </p>
                        ) : (
                            <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                                {filteredFunnels.map((f) => {
                                    const sel = selectedFunnels.has(f.id);
                                    return (
                                        <button
                                            key={f.id} type="button" onClick={() => toggleFunnel(f.id)}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                                                sel ? 'bg-violet-50' : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className={`flex h-4 w-4 items-center justify-center rounded border shrink-0 ${
                                                sel ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                                            }`}>
                                                {sel && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                            <span className="flex-1 min-w-0 truncate">{f.nome}</span>
                                            {!f.is_active && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                                                    inativo
                                                </span>
                                            )}
                                            <span className="text-[10px] text-gray-400 shrink-0 whitespace-nowrap">
                                                {f.node_count} {f.node_count === 1 ? 'modelo' : 'modelos'}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => mutation.mutate()}
                        disabled={selectedFunnels.size === 0 || mutation.isPending}
                    >
                        {mutation.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        Sincronizar {totalNodes > 0 ? `(${totalNodes})` : ''}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
